import React, { useEffect, useMemo, useState } from 'react';
import {
  collection,
  addDoc,
  serverTimestamp,
  query,
  orderBy,
  getDocs,
  doc,
  updateDoc,
} from 'firebase/firestore';
import { db } from '../../firebase';
import { useUser } from '../../context/UserContext';

const defaultForm = {
  title: '',
  description: '',
  priority: 'media',
};

const PRIORITY_COLORS = {
  baja: 'bg-gray-100 text-gray-700',
  media: 'bg-blue-100 text-blue-700',
  alta: 'bg-amber-100 text-amber-700',
  critica: 'bg-red-100 text-red-700',
};

const STATUS_LABELS = {
  abierto: 'Abierto',
  en_proceso: 'En proceso',
  en_espera_cliente: 'En espera',
  resuelto: 'Resuelto',
  cerrado: 'Cerrado',
};

const ClientService = () => {
  const { clientProfile } = useUser();
  const [form, setForm] = useState(defaultForm);
  const [tickets, setTickets] = useState([]);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [timeline, setTimeline] = useState([]);
  const [note, setNote] = useState('');
  const [loadingTickets, setLoadingTickets] = useState(false);
  const [loadingTimeline, setLoadingTimeline] = useState(false);
  const clientId = clientProfile?.id;

  const priorityOptions = useMemo(
    () => [
      { value: 'baja', label: 'Baja' },
      { value: 'media', label: 'Media' },
      { value: 'alta', label: 'Alta' },
      { value: 'critica', label: 'Crítica' },
    ],
    []
  );

  useEffect(() => {
    if (!clientId) return;
    const fetchTickets = async () => {
      setLoadingTickets(true);
      try {
        const q = query(
          collection(db, 'clients', clientId, 'serviceTickets'),
          orderBy('createdAt', 'desc')
        );
        const snap = await getDocs(q);
        setTickets(
          snap.docs.map((d) => ({ id: d.id, ...d.data() }))
        );
      } catch (err) {
        console.error('❌ Error cargando tickets del cliente:', err);
      } finally {
        setLoadingTickets(false);
      }
    };
    fetchTickets();
  }, [clientId]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!clientId || !form.title.trim()) return;

    const payload = {
      title: form.title.trim(),
      description: form.description.trim(),
      priority: form.priority,
      status: 'abierto',
      clientId,
      clientName: clientProfile?.businessName || clientProfile?.name || 'Cliente',
      clientEmail: clientProfile?.email || '',
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    };

    try {
      const ticketRef = await addDoc(collection(db, 'clients', clientId, 'serviceTickets'), payload);
      await addDoc(collection(db, 'clients', clientId, 'serviceTickets', ticketRef.id, 'timeline'), {
        summary: 'Ticket creado por el cliente',
        detail: payload.description,
        channel: 'cliente',
        priority: payload.priority,
        entityType: 'serviceTicket',
        ticketTitle: payload.title,
        clientName: payload.clientName,
        createdBy: payload.clientEmail,
        createdAt: serverTimestamp(),
      });
      setForm(defaultForm);
      setTickets((prev) => [{ id: ticketRef.id, ...payload }, ...prev]);
    } catch (err) {
      console.error('❌ Error creando ticket:', err);
    }
  };

  const openTicket = async (ticket) => {
    if (!clientId) return;
    setSelectedTicket(ticket);
    setLoadingTimeline(true);
    try {
      const q = query(
        collection(db, 'clients', clientId, 'serviceTickets', ticket.id, 'timeline'),
        orderBy('createdAt', 'desc')
      );
      const snap = await getDocs(q);
      setTimeline(
        snap.docs.map((d) => ({ id: d.id, ...d.data() }))
      );
    } catch (err) {
      console.error('❌ Error cargando timeline del ticket:', err);
    } finally {
      setLoadingTimeline(false);
    }
  };

  const addNote = async () => {
    if (!clientId || !selectedTicket || !note.trim()) return;
    try {
      await addDoc(collection(db, 'clients', clientId, 'serviceTickets', selectedTicket.id, 'timeline'), {
        summary: note.trim(),
        channel: 'cliente',
        entityType: 'serviceTicket',
        ticketTitle: selectedTicket.title,
        clientName: clientProfile?.businessName || clientProfile?.name || 'Cliente',
        createdBy: clientProfile?.email || 'cliente',
        createdAt: serverTimestamp(),
      });
      await updateDoc(doc(db, 'clients', clientId, 'serviceTickets', selectedTicket.id), {
        updatedAt: serverTimestamp(),
        lastClientMessageAt: serverTimestamp(),
      });
      setNote('');
      openTicket(selectedTicket);
    } catch (err) {
      console.error('❌ Error añadiendo nota:', err);
    }
  };

  const markResolved = async () => {
    if (!clientId || !selectedTicket) return;
    try {
      await updateDoc(doc(db, 'clients', clientId, 'serviceTickets', selectedTicket.id), {
        status: 'resuelto',
        updatedAt: serverTimestamp(),
      });
      setTickets((prev) =>
        prev.map((t) => (t.id === selectedTicket.id ? { ...t, status: 'resuelto' } : t))
      );
      setSelectedTicket((prev) => prev && { ...prev, status: 'resuelto' });
    } catch (err) {
      console.error('❌ Error actualizando ticket:', err);
    }
  };

  return (
    <div className="space-y-8">
      <section className="bg-white rounded-lg shadow p-6">
        <h2 className="text-lg font-semibold text-gray-800 mb-4">Solicitar servicio</h2>
        <form className="grid gap-4" onSubmit={handleCreate}>
          <input
            type="text"
            name="title"
            value={form.title}
            onChange={handleChange}
            placeholder="Título del servicio"
            className="border rounded px-3 py-2"
          />
          <textarea
            name="description"
            value={form.description}
            onChange={handleChange}
            placeholder="Describe lo que necesitas"
            className="border rounded px-3 py-2 min-h-[120px]"
          />
          <div>
            <label className="text-sm font-medium text-gray-600">Prioridad</label>
            <select
              name="priority"
              value={form.priority}
              onChange={handleChange}
              className="border rounded px-3 py-2 w-full mt-1"
            >
              {priorityOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
          <button
            type="submit"
            className="bg-blue-600 text-white px-4 py-2 rounded-lg font-medium hover:bg-blue-700"
          >
            Crear ticket
          </button>
        </form>
      </section>

      <section className="bg-white rounded-lg shadow p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-gray-800">Mis tickets</h2>
          {loadingTickets && <p className="text-sm text-gray-500">Cargando…</p>}
        </div>
        {tickets.length === 0 ? (
          <p className="text-sm text-gray-500">Aún no has creado tickets de servicio.</p>
        ) : (
          <div className="space-y-3">
            {tickets.map((ticket) => (
              <article
                key={ticket.id}
                className="border rounded-lg px-4 py-3 flex flex-col gap-2 cursor-pointer hover:bg-gray-50"
                onClick={() => openTicket(ticket)}
              >
                <div className="flex items-center justify-between">
                  <h3 className="font-semibold text-gray-800">{ticket.title}</h3>
                  <span
                    className={`text-xs px-2 py-1 rounded-full ${
                      PRIORITY_COLORS[ticket.priority] || 'bg-gray-100'
                    }`}
                  >
                    {ticket.priority?.toUpperCase()}
                  </span>
                </div>
                <p className="text-sm text-gray-600 line-clamp-2">{ticket.description}</p>
                <div className="flex items-center gap-4 text-xs text-gray-500">
                  <span className="font-medium">
                    {STATUS_LABELS[ticket.status] || ticket.status}
                  </span>
                  {ticket.updatedAt?.toDate && (
                    <span>
                      Actualizado {ticket.updatedAt.toDate().toLocaleDateString('es-PR', {
                        month: 'short',
                        day: 'numeric',
                      })}
                    </span>
                  )}
                </div>
              </article>
            ))}
          </div>
        )}
      </section>

      {selectedTicket && (
        <section className="bg-white rounded-lg shadow p-6 space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-lg font-semibold text-gray-800">{selectedTicket.title}</h3>
              <p className="text-sm text-gray-500">
                Estado: {STATUS_LABELS[selectedTicket.status] || selectedTicket.status}
              </p>
            </div>
            {selectedTicket.status !== 'resuelto' && (
              <button
                type="button"
                onClick={markResolved}
                className="px-4 py-2 rounded-lg bg-emerald-100 text-emerald-700 text-sm font-medium"
              >
                Marcar como resuelto
              </button>
            )}
          </div>

          <div>
            <h4 className="text-sm font-semibold text-gray-700 mb-2">Línea de tiempo</h4>
            {loadingTimeline ? (
              <p className="text-sm text-gray-500">Cargando actualizaciones…</p>
            ) : timeline.length === 0 ? (
              <p className="text-sm text-gray-500">Sin notas todavía.</p>
            ) : (
              <ul className="space-y-3 max-h-64 overflow-y-auto pr-2">
                {timeline.map((entry) => (
                  <li key={entry.id} className="border rounded-lg px-3 py-2">
                    <p className="text-sm text-gray-800">{entry.summary}</p>
                    <div className="flex items-center justify-between text-xs text-gray-500 mt-1">
                      <span>{entry.createdBy || entry.channel}</span>
                      {entry.createdAt?.toDate && (
                        <span>
                          {entry.createdAt
                            .toDate()
                            .toLocaleString('es-PR', { hour: '2-digit', minute: '2-digit', month: 'short', day: 'numeric' })}
                        </span>
                      )}
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>

          <div className="space-y-2">
            <textarea
              className="border rounded px-3 py-2 w-full"
              rows={3}
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="Agrega una nota o actualiza al equipo"
            />
            <button
              type="button"
              onClick={addNote}
              className="bg-blue-600 text-white px-4 py-2 rounded-lg text-sm font-medium"
            >
              Enviar nota
            </button>
          </div>
        </section>
      )}
    </div>
  );
};

export default ClientService;
